import type { DataSchema, Schema } from 'types';
import type { Table, GroupingState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { memo, useState } from 'react';
import { ListItemText, Menu, MenuItem } from '@mui/material';
import { GroupWork } from '@mui/icons-material';
import { Button } from 'components/mui/common/Button';
import { Checkbox } from 'components/mui/common/Checkbox';

const ColumnGroupingControlerBase = <T extends keyof P, P extends DataSchema>({
  tableInstance,
  dataTestId,
  testIdPrefix,
}: {
  tableInstance: Table<Schema<T, P>>;
  dataTestId?: string;
  /**
   * This prop is needed to trigger a re-render when column grouping changes.
   * It is not used in the function body, but its presence in the props object ensures that
   * React.memo detects a change and updates the component.
   */
  columnGrouping?: GroupingState;
  testIdPrefix?: string;
}) => {
  const resolvedDataTestId = dataTestId ?? (testIdPrefix ? `${testIdPrefix}-grouping-button` : 'table-grouping-button');
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        data-testid={resolvedDataTestId}
        onClick={handleClick}
        variant="outlined"
        type="button"
        sx={{ paddingX: '24px' }}
        startIcon={<GroupWork />}>
        {t('common.grouping')}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{ marginTop: '4px' }}
        MenuListProps={{ sx: { marginY: '4px', maxHeight: '240px', overflow: 'auto', textOverflow: 'ellipsis' } }}>
        {tableInstance.getAllLeafColumns().map((column) => {
          const header = column.columnDef.header;
          const label = typeof header === 'string' ? header : column.id;

          if (column.id === 'select' || column.id === 'actions' || column.id === 'collapse' || column.id === 'id' || column.id === 'hidden') {
            return null;
          }

          if (!column.getCanGroup()) return null;

          return (
            <MenuItem key={column.id} onClick={column.getToggleGroupingHandler()}>
              <Checkbox checked={column.getIsGrouped()} />
              <ListItemText primary={label} />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export const ColumnGroupingControler = memo(ColumnGroupingControlerBase) as unknown as typeof ColumnGroupingControlerBase;
